/**
 * AgentModelModal — the ONE shared "Agent & model" editor.
 *
 * Used by BOTH surfaces that change an agent/model + credential:
 *   - PIPELINES (PipelineDetailPage): full agent:model SSOT picker
 *     (`AgentModelSelect` seeded from `useAgentOptions()`), Claude gets a
 *     separate API-key + OAuth-token field. Caller's onSave PATCHes
 *     /workflows/{uuid} with {aiAgent, aiModel, aiApiKey, aiOAuthToken}.
 *   - APPS (AppDetailPage): vendor-only select (Claude / Codex), single
 *     credential field. Caller's onSave POSTs Apps.setAgent({provider, token}).
 *
 * The two used to be separate components that drifted visually. This is the
 * single source of truth for the chrome (mkt-modal), the field styling
 * (atomic Field + apps-modal-field-label), the validation gate, and the
 * masked-credential handling. The PARENT owns the request shape via `onSave`.
 *
 * Credentials arrive MASKED from the GET (sk-a...wxyz). We seed the field
 * with the mask and treat a value still containing '...' as "untouched" —
 * never sending it. A real change (or a vendor switch that empties the seed)
 * is validated by format before submit.
 *
 * Validation (the hard requirement — empty/invalid must NOT save):
 *   - A token is REQUIRED when there's no stored masked token to keep for the
 *     selected agent/provider. Empty → inline "API key required", no submit.
 *   - A TYPED token (not the mask) is format-checked:
 *       claude  → ^sk-ant-(oat01|api03)-   (api03 = API key, oat01 = OAuth)
 *       codex   → ^sk-
 *   - Save is disabled while invalid; submit shows the inline error.
 *   - Server errors map onto the same inline field slot (short, red).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { IonIcon } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import Field from '../../atoms/Field/Field';
import AgentModelSelect from '../../atoms/AgentModelSelect/AgentModelSelect';
import { parseAgentOption } from '../../../utils/agent-options';
// Chrome classes (mkt-modal-overlay / mkt-modal / header / title / close /
// body / deploy / secondary) come from the marketplace detail modal stylesheet.
import '../../../pages/MarketplacePage/MarketplaceDetailModal.css';
import '../../atoms/Field/Field.css';
import './AgentModelModal.css';

// Credential format gates. claude api03 = standard API key, oat01 = Pro/Max
// OAuth token — both are valid in the single Claude key field.
const CLAUDE_KEY_RE = /^sk-ant-(oat01|api03)-/;
const OPENAI_KEY_RE = /^sk-/;
const CURSOR_KEY_RE = /^key_/;
const GEMINI_KEY_RE = /^AIza/;
const CLAUDE_OAUTH_RE = /^sk-ant-oat01-/;

const VENDOR_KEY_HINT = {
  claude: 'sk-ant-…',
  codex: 'sk-…',
  cursor: 'key_…',
  gemini: 'AIza…',
};
const VENDOR_KEY_LABEL = {
  claude: 'API key / OAuth token',
  codex: 'OpenAI API key',
  cursor: 'Cursor API key',
  gemini: 'Google API key',
};

// A value still carrying the '...' mask is the untouched server seed — the
// user hasn't typed a real key, so we keep (don't send) it.
const isMask = (v) => typeof v === 'string' && v.includes('...');

/**
 * Validate one credential field for a vendor.
 * @returns {string} '' when valid, else a SHORT inline error message.
 */
function validateToken({ vendor, value, maskedSeed, required = true }) {
  // Defensive: seeds can arrive as null (GET returns null, not '') — coerce
  // so .trim() never throws.
  const s = typeof value === 'string' ? value : '';
  const typed = !isMask(s) && s.trim() !== '';
  // Untouched mask → keeping the stored key. Valid.
  if (isMask(s)) return '';
  // Empty + nothing stored to keep → required error.
  if (s.trim() === '') {
    if (required && !maskedSeed) return 'API key required';
    return '';
  }
  if (!typed) return '';
  const v = s.trim();
  if (vendor === 'claude') {
    if (!CLAUDE_KEY_RE.test(v)) return 'Key must start with sk-ant-…';
  } else if (vendor === 'cursor') {
    if (!CURSOR_KEY_RE.test(v)) return 'Key must start with key_…';
  } else if (vendor === 'gemini') {
    if (!GEMINI_KEY_RE.test(v)) return 'Key must start with AIza…';
  } else {
    // codex / openai
    if (!OPENAI_KEY_RE.test(v)) return 'Key must start with sk-…';
  }
  return '';
}

export default function AgentModelModal({
  isOpen,
  onClose,
  title = 'Model & engine',
  variant,
  agentOptions = [],
  seedValue = '',
  seedKey = '',
  seedOAuth = '',
  onSave,
  onSaved,
  setToast,
}) {
  // `variant` decides the agent control: 'pipeline' = full agent:model
  // SSOT picker; 'app' = vendor-only (Claude / Codex) select.
  const isPipeline = variant === 'pipeline';

  // Coerce seeds to strings — callers pass row fields that can be null OR a
  // non-string (which would render as "[object Object]" in the input).
  const str = (x) => (typeof x === 'string' ? x : '');
  const [value, setValue] = useState(str(seedValue));          // pipeline: 'claude:opus-4.8'
  const [provider, setProvider] = useState(str(seedValue) || 'claude'); // app: 'claude'|'codex'
  const [apiKey, setApiKey] = useState(str(seedKey));
  const [oauth, setOauth] = useState(str(seedOAuth));
  const [saving, setSaving] = useState(false);
  const [keyErr, setKeyErr] = useState('');   // inline error on the key field
  const [submitted, setSubmitted] = useState(false);
  // Credential fields start readOnly so the browser can't autofill a saved
  // password into them (decoys + autoComplete aren't enough in Chrome). They
  // unlock on first focus — by then autofill has already passed.
  const [credUnlocked, setCredUnlocked] = useState(false);

  // (Re)seed on open / when the underlying values change.
  useEffect(() => {
    if (!isOpen) return;
    setValue(str(seedValue));
    setProvider(str(seedValue) || 'claude');
    setApiKey(str(seedKey));
    setOauth(str(seedOAuth));
    setCredUnlocked(false);
    setKeyErr('');
    setSubmitted(false);
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, seedValue, seedKey, seedOAuth]);

  // Selected vendor — drives which credential fields show + how we validate.
  const vendor = useMemo(() => {
    if (isPipeline) return parseAgentOption(value).aiAgent;
    return provider;
  }, [isPipeline, value, provider]);

  const isClaude = vendor === 'claude';
  // EVERY agent here is BYOK and needs a key — claude (sk-ant-…), cursor
  // (key_…), codex (sk-…), gemini (AIza…). Only the "project default" empty
  // option (no vendor) needs none.
  const needsKey = Boolean(vendor);

  // The masked seed we'd keep if the user doesn't type. Switching vendor away
  // from the seeded one empties it (the stored token belongs to the other
  // vendor) — so a fresh key becomes required for the new vendor.
  const seededVendor = isPipeline
    ? parseAgentOption(str(seedValue)).aiAgent
    : (str(seedValue) || 'claude');
  const maskedSeed = vendor === seededVendor ? str(seedKey) : '';

  // When the user switches vendor, the stored key (for the old vendor) no
  // longer applies — clear it (or restore the mask if they switched back to
  // the seeded vendor) so the field reflects what we'd actually use.
  const prevVendorRef = useRef(vendor);
  useEffect(() => {
    if (prevVendorRef.current === vendor) return;
    prevVendorRef.current = vendor;
    setApiKey(vendor === seededVendor ? str(seedKey) : '');
    setOauth(vendor === seededVendor ? str(seedOAuth) : '');
    setKeyErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor]);

  // Live validity — gates the Save button.
  const tokenError = needsKey
    ? validateToken({ vendor, value: apiKey, maskedSeed })
    : '';
  // OAuth is optional everywhere; only format-check a typed value.
  const oauthError = (isPipeline && isClaude && !isMask(oauth) && oauth.trim() !== '')
    ? (CLAUDE_OAUTH_RE.test(oauth.trim()) ? '' : 'OAuth starts sk-ant-oat01-')
    : '';
  const isValid = !tokenError && !oauthError;

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, saving, onClose]);

  if (!isOpen) return null;

  const tokenIsMask = isMask(apiKey);
  const oauthIsMask = isMask(oauth);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSubmitted(true);
    if (!isValid) {
      // Surface the gating error inline (the field is already error-styled
      // because submitted flips on).
      setKeyErr(tokenError || oauthError);
      return;
    }
    setKeyErr('');

    // Build the surface-specific body. Only send a credential the user
    // actually changed (differs from the masked seed AND isn't the mask).
    let body;
    if (isPipeline) {
      const { aiAgent, aiModel } = parseAgentOption(value);
      body = { aiAgent: aiAgent || null, aiModel: aiModel || null };
      if (needsKey && apiKey !== maskedSeed && !tokenIsMask) {
        body.aiApiKey = apiKey.trim() === '' ? null : apiKey.trim();
      }
      if (isClaude && oauth !== seedOAuth && !oauthIsMask) {
        body.aiOAuthToken = oauth.trim() === '' ? null : oauth.trim();
      }
    } else {
      body = { provider };
      if (apiKey !== maskedSeed && !tokenIsMask && apiKey.trim() !== '') {
        body.token = apiKey.trim();
      }
    }

    setSaving(true);
    setKeyErr('');
    try {
      await onSave(body);
      setToast?.({ show: true, isOpen: true, message: 'Model updated', color: 'success' });
      onSaved?.();
      onClose?.();
    } catch (e2) {
      // Map the server error onto the field inline (short).
      setKeyErr(e2?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // The error shown on the key field: the live gate error after a submit
  // attempt, otherwise the server error (if any).
  const shownKeyErr = (submitted && tokenError) ? tokenError : keyErr;

  return createPortal(
    <div className="mkt-modal-overlay" role="presentation" onClick={() => !saving && onClose?.()}>
      <div
        className="mkt-modal agent-model-modal"
        style={{ maxWidth: 460 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-model-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="mkt-modal-close"
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
        >
          <IonIcon icon={closeOutline} />
        </button>

        <div className="mkt-modal-body">
          <header className="mkt-modal-header">
            <h2 className="mkt-modal-title" id="agent-model-title">{title}</h2>
          </header>

          <form onSubmit={handleSave} autoComplete="off" className="agent-model-form">
            {/* Off-screen decoys soak up the browser's saved-login autofill so
                it can't dump a saved password into the token field. */}
            <input type="text" name="zibby-decoy-user" tabIndex={-1} autoComplete="username"
              aria-hidden="true" style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />
            <input type="password" name="zibby-decoy-pass" tabIndex={-1} autoComplete="new-password"
              aria-hidden="true" style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />

            <div className="agent-model-field">
              <label className="apps-modal-field-label" htmlFor="agent-model-pick">Model &amp; engine</label>
              {isPipeline ? (
                <AgentModelSelect
                  id="agent-model-pick"
                  options={agentOptions}
                  value={value}
                  onChange={(v) => { setValue(v); setKeyErr(''); }}
                  className="mkt-field mkt-field--select"
                  ariaLabel="Model and engine for this agent"
                />
              ) : (
                <select
                  id="agent-model-pick"
                  className="mkt-field mkt-field--select"
                  value={provider}
                  onChange={(e) => { setProvider(e.target.value); setKeyErr(''); }}
                  disabled={saving}
                  aria-label="Model provider for this app"
                >
                  <option value="claude">Claude</option>
                  <option value="codex">Codex</option>
                </select>
              )}
            </div>

            {needsKey && (
              <div className="agent-model-field">
                <label className="apps-modal-field-label" htmlFor="agent-model-key">
                  {VENDOR_KEY_LABEL[vendor] || 'API key'}
                </label>
                <Field
                  as="input"
                  id="agent-model-key"
                  /* Always text — the prefilled MASK (sk-a...wxyz) stays
                     readable so the user sees the stored key's suffix, and a
                     newly-pasted key is visible to verify (same as Stripe /
                     OpenAI key inputs). readOnly-until-focus blocks autofill. */
                  type="text"
                  name="zibby-agent-token"
                  value={apiKey}
                  placeholder={VENDOR_KEY_HINT[vendor] || 'API key'}
                  onChange={(e) => { setApiKey(e.target.value); if (keyErr) setKeyErr(''); }}
                  readOnly={!credUnlocked}
                  onFocus={() => setCredUnlocked(true)}
                  autoComplete="new-password"
                  data-1p-ignore="true"
                  data-lpignore="true"
                  spellCheck={false}
                  disabled={saving}
                  status={shownKeyErr ? 'error' : null}
                  aria-invalid={shownKeyErr ? true : undefined}
                />
                {shownKeyErr && <p className="agent-model-field-error">{shownKeyErr}</p>}

                {isPipeline && isClaude && (
                  <>
                    <label className="apps-modal-field-label agent-model-sublabel" htmlFor="agent-model-oauth">
                      Claude OAuth token (Pro/Max)
                    </label>
                    <Field
                      as="input"
                      id="agent-model-oauth"
                      type="text"
                      name="zibby-agent-oauth"
                      value={oauth}
                      placeholder="sk-ant-oat01-…"
                      onChange={(e) => { setOauth(e.target.value); if (keyErr) setKeyErr(''); }}
                      readOnly={!credUnlocked}
                      onFocus={() => setCredUnlocked(true)}
                      autoComplete="new-password"
                      data-1p-ignore="true"
                      data-lpignore="true"
                      spellCheck={false}
                      disabled={saving}
                      status={(submitted && oauthError) ? 'error' : null}
                      aria-invalid={(submitted && oauthError) ? true : undefined}
                    />
                    {submitted && oauthError && <p className="agent-model-field-error">{oauthError}</p>}
                  </>
                )}
              </div>
            )}

            <div className="mkt-alert-modal-actions">
              <button
                type="button"
                onClick={handleSave}
                className="mkt-modal-deploy mkt-modal-deploy--full"
                disabled={saving || !isValid}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="mkt-alert-modal-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body,
  );
}

AgentModelModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  /** 'pipeline' = agent:model SSOT picker; 'app' = vendor-only select. */
  variant: PropTypes.oneOf(['pipeline', 'app']).isRequired,
  /** Pipeline only: [{ value, label }] from useAgentOptions(). */
  agentOptions: PropTypes.array,
  /** Pipeline: 'claude:opus-4.8'. App: 'claude' | 'codex'. */
  seedValue: PropTypes.string,
  /** Masked stored key, e.g. 'sk-a...wxyz', or ''. */
  seedKey: PropTypes.string,
  /** Pipeline+Claude only: masked stored OAuth token, or ''. */
  seedOAuth: PropTypes.string,
  /** Caller does the request. Receives the surface-specific body. */
  onSave: PropTypes.func.isRequired,
  /** Called after a successful onSave (refresh). */
  onSaved: PropTypes.func,
  setToast: PropTypes.func,
};
